function animateCartRowRemove(row) {
    return new Promise((resolve) => {
        if (!row) {
            resolve();
            return;
        }

        const height = row.getBoundingClientRect().height;
        row.style.overflow = 'hidden';
        row.style.height = `${height}px`;
        row.style.opacity = '1';
        row.style.transition = 'opacity 0.22s ease, height 0.28s ease, margin 0.28s ease, padding 0.28s ease';

        requestAnimationFrame(() => {
            row.style.opacity = '0';
            row.style.height = '0px';
            row.style.marginTop = '0px';
            row.style.marginBottom = '0px';
            row.style.paddingTop = '0px';
            row.style.paddingBottom = '0px';
        });

        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            resolve();
        };

        row.addEventListener('transitionend', finish, { once: true });
        setTimeout(finish, 320);
    });
}

class CartDrawer extends HTMLElement {
    constructor() {
        super();
        this.sectionId = 'cart-drawer-section';
        this.isUpdating = false;

        this.onBodyClick = this.onBodyClick.bind(this);
        this.onDrawerClick = this.onDrawerClick.bind(this);
        this.onCartChanged = this.onCartChanged.bind(this);
    }

    connectedCallback() {
        this.addEventListener('click', this.onDrawerClick);
        document.addEventListener('click', this.onBodyClick);
        document.addEventListener('cart:item-added', this.onCartChanged);
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.onDrawerClick);
        document.removeEventListener('click', this.onBodyClick);
        document.removeEventListener('cart:item-added', this.onCartChanged);
    }

    onCartChanged() {
        this.refreshUI();
    }

    onBodyClick(e) {
        if (e.target.closest('[data-cart-btn]')) {
            e.preventDefault();
            this.open();
        }

        if (e.target === this) this.close();
    }

    onDrawerClick(e) {
        if (e.target.closest('[data-cart-close]')) {
            this.close();
            return;
        }

        const actionBtn = e.target.closest('[data-action="plus"], [data-action="minus"]');
        if (actionBtn) {
            const line = actionBtn.dataset.line;
            let quantity = parseInt(actionBtn.dataset.quantity);
            
            if (actionBtn.dataset.action === 'plus') {
                const maxQuantity = parseInt(actionBtn.dataset.maxQuantity) || 9999;
                if (quantity >= maxQuantity) return; 
                quantity += 1;
            } else {
                quantity -= 1;
            }
            
            this.updateItem(line, quantity, actionBtn);
            return;
        }

        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
            this.updateItem(removeBtn.dataset.line, 0, removeBtn);
            return;
        }

        const variantOption = e.target.closest('[data-dropdown-option]');
        if (variantOption && variantOption.hasAttribute('data-variant-id') && !variantOption.disabled) {
            if (!variantOption.classList.contains('active')) {
                this.changeVariant(
                    variantOption.dataset.line,
                    1, 
                    variantOption.dataset.variantId
                );
            }
        }
    }

    open() {
        this.style.display = 'flex';
        this.refreshUI();
        if (typeof swipers !== 'undefined') {
            swipers.forEach(swiper => swiper.update());
        }
        
        this.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    }

    close() {
        this.style.display = 'none';
        
        this.dispatchEvent(new CustomEvent('cart:close', { bubbles: true }));
    }

    async updateItem(line, quantity, triggerEl) {
        if (this.isUpdating) return;
        this.isUpdating = true;

        try {
            if (quantity <= 0) {
                await animateCartRowRemove(triggerEl?.closest('li'));
            }

            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line, quantity })
            });
            await this.refreshUI();
        } catch (error) {
            console.error('Error updating cart:', error);
        } finally {
            this.isUpdating = false;
        }
    }

    async changeVariant(line, quantity, newVariantId) {
        if (this.isUpdating) return;
        this.isUpdating = true;

        try {

            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line, quantity: 0 })
            });
            

            await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: newVariantId, quantity })
            });
            
            await this.refreshUI();
        } catch (error) {
            console.error('Error changing variant:', error);
        } finally {
            this.isUpdating = false;
        }
    }

    async refreshUI() {
        try {
            const root = window.Shopify?.routes?.root || '/';
            const res = await fetch(`${root}?section_id=${this.sectionId}`);
            const text = await res.text();
            
            const parser = new DOMParser();
            const htmlDocument = parser.parseFromString(text, 'text/html');
            const newCartDrawer = htmlDocument.querySelector('cart-drawer');

            if (newCartDrawer) {
                this.innerHTML = newCartDrawer.innerHTML;
            }

            if (typeof swipers !== 'undefined') {
                swipers.forEach(swiper => swiper.update());
            }

            this.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
            
        } catch (error) {
            console.error('Error refreshing cart HTML:', error);
        }
    }
}

customElements.define('cart-drawer', CartDrawer);